import { motion } from "framer-motion";
import { Camera as CameraIcon, Images as ImagesIcon, Link2, FolderPlus } from "lucide-react";
import { cn } from "@/lib/utils";
import { ADD_MENU_ITEMS, type MenuItemKey } from "./addContentFlowModel";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { ROW_TITLE } from "@/lib/typography";

const MENU_ICONS: Record<MenuItemKey, typeof CameraIcon> = {
  collection: FolderPlus,
  photo: CameraIcon,
  images: ImagesIcon,
  link: Link2,
};

interface AddContentFlowMenuProps {
  onSelect: (key: MenuItemKey) => void;
  /** "stack" = labeled bubbles rising above the desktop FAB; "row" = compact
      material card above the mobile nav. */
  layout?: "stack" | "row" | "menu";
}

export function AddContentFlowMenu({ onSelect, layout = "stack" }: AddContentFlowMenuProps) {
  if (layout === "menu") {
    return ADD_MENU_ITEMS.map(item => {
      const Icon = MENU_ICONS[item.key];
      return (
        <DropdownMenuItem key={item.key} onSelect={() => onSelect(item.key)}>
          <Icon aria-hidden="true" />
          {item.label}
        </DropdownMenuItem>
      );
    });
  }
  if (layout === "row") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 16, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 16, scale: 0.95 }}
        transition={{ duration: 0.18, ease: "easeOut" }}
        className={cn(
          "flex items-center gap-1 rounded-2xl p-1.5 shadow-lg border border-border/60",
          "bg-card supports-[backdrop-filter]:bg-card/90 supports-[backdrop-filter]:backdrop-blur-xl",
        )}
      >
        {ADD_MENU_ITEMS.map(item => {
          const Icon = MENU_ICONS[item.key];
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => onSelect(item.key)}
              className={cn(
                "flex w-16 flex-col items-center gap-1 rounded-xl px-2 py-2",
                "transition-transform active:scale-95",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              )}
              aria-label={item.label}
            >
              <Icon className="h-6 w-6 text-foreground" />
              <span className="text-ui-micro font-medium text-muted-foreground">{item.label}</span>
            </button>
          );
        })}
      </motion.div>
    );
  }

  // Stack: buttons stay on one vertical line (items-end); the caller's wrapper
  // decides where the block sits.
  return (
    <div className="flex flex-col gap-3 items-end">
      {ADD_MENU_ITEMS.map((item, index) => {
        const Icon = MENU_ICONS[item.key];
        const reverseIndex = ADD_MENU_ITEMS.length - 1 - index;
        return (
          <motion.div
            key={item.key}
            initial={{ opacity: 0, y: 20, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.8 }}
            transition={{ duration: 0.2, delay: reverseIndex * 0.05 }}
            className="flex items-center gap-3"
          >
            <span className={cn(ROW_TITLE, "text-white drop-shadow-md")}>{item.label}</span>
            <button
              type="button"
              onClick={() => onSelect(item.key)}
              className={cn(
                "w-[50px] h-[50px] rounded-full bg-white text-gray-900",
                "flex items-center justify-center",
                "transition-transform duration-150 active:scale-90",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                "shadow-lg",
              )}
              aria-label={item.label}
            >
              <Icon className="w-[25px] h-[25px]" />
            </button>
          </motion.div>
        );
      })}
    </div>
  );
}
